import React, { useEffect } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';


import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { Flex, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { usePlatformInstrumentation } from '@atlassian/platform-analytics';

import { PlaylistErrorIllustration } from '../assets/PlaylistErrorIllustration';

const messages = defineMessages({
	errorPlaylistHeading: {
		id: 'ai-audio.playlist.error.heading',
		defaultMessage: 'Hmm, something went wrong',
		description: 'Heading shown when the playlist fails to load',
	},
	errorPlaylistMessage: {
		id: 'ai-audio.playlist.error.message',
		defaultMessage: 'Failed to load playlist',
		description: 'Default error message shown when the playlist fails to load',
	},
	tryAgainButton: {
		id: 'ai-audio.playlist.error.try-again',
		defaultMessage: 'Try again',
		description: 'Button text to retry loading the playlist',
	},
});

const styles = cssMap({
	container: {
		flex: 1,
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
	content: {
		width: '290px',
		textAlign: 'center',
	},
});

interface PlaylistErrorViewProps {
	error?: Error;
	onRetry?: () => void;
}

export const PlaylistErrorView = ({
	error,
	onRetry,
}: PlaylistErrorViewProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { sendTrackEvent } = usePlatformInstrumentation();

	useEffect(() => {
		sendTrackEvent({
			eventType: 'track',
			action: 'viewed',
			actionSubject: 'playlistError',
			actionSubjectId: 'playlist',
			source: 'playlist',
			attributes: {
				message: error?.message,
			},
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	return (
		<Flex
			xcss={styles.container}
			justifyContent="center"
			alignItems="center"
			rowGap="space.200"
			direction="column"
		>
			<PlaylistErrorIllustration />
			<Flex xcss={styles.content} alignItems="center" rowGap="space.150" direction="column">
				<Heading size="small" as="h3">
					{formatMessage(messages.errorPlaylistHeading)}
				</Heading>
				<Text size="medium" color="color.text.subtlest">
					{error?.message || formatMessage(messages.errorPlaylistMessage)}
				</Text>
			</Flex>
			{onRetry && (
				<Button appearance="default" onClick={onRetry}>
					{formatMessage(messages.tryAgainButton)}
				</Button>
			)}
		</Flex>
	);
};
